import {
  action,
  AnnotationsMap,
  computed,
  makeObservable,
  observable,
  runInAction,
} from 'mobx';
import moment from 'moment/moment';
import {EAutocompleteOption} from 'webapp-lib/Components';
import {showSnackbar, SnackbarType} from 'webapp-lib/Functions';
import {downloadGeneratedFile} from 'webapp-lib/utils';

import {Folder} from '#/FolderTree';
import documentsBackend, {
  CreateDocumentResponse,
  DefaultValueInResponse,
  NonUniqueItemNameCondition,
} from '@lexian/api/documents-api';
import {TranslatedLocale} from '@lexian/api/support-api';
import i18n from '_/i18n';
import {
  ChooseTemplateData,
  TemplateWizardStep,
} from '_/store/template/template.types';
import {getCreateTemporaryDocumentParams} from '_/store/template/template.utils';
import TemplateBaseStore from '_/store/template/templateBase.store';
import {unexpectedError} from '_/utilities';
import {
  isNotNullOrUndefined,
  Loadable,
  LoggedInUserInformation,
  State,
} from '_/utilities/types';

type TemplateFile = {
  id: string;
  locale: string;
  title: string;
  type?: 'EMAIL' | 'EXCEL' | 'POWERPOINT' | 'WORD' | 'ZIP';
};

type Template = {
  id: string;
  templateFiles: TemplateFile[];
  text: string;
};

type ChooseDocumentTemplateData = Required<
  Omit<ChooseTemplateData, 'casusId'>
> & {
  casusId?: string;
  folderId?: string;
  fileName: string;
  logDescription?: string;
};

type GetTemplatesPayLoad =
  | Parameters<typeof documentsBackend.getReportTemplates>[0]
  | Parameters<typeof documentsBackend.getDocumentTemplates>[0];

const createDocumentFromTemplateStoreMobXAnnotations: AnnotationsMap<
  | CreateDocumentFromTemplateStore
  | 'fileNameModified'
  | 'logDescriptionModified',
  never
> = {
  folderLoadable: observable,
  templateLoadable: observable,
  chooseTemplateData: observable,
  isCreatingDocument: observable,
  fetchFolderOverview: action.bound,
  getTemplateFile: action.bound,
  getLocalesForSelectedTemplate: action.bound,
  setFileNameHasBeenModified: action.bound,
  setChooseTemplateData: action.bound,
  getOverridableTemplatePlaceholders: action.bound,
  fetchDependablePlaceholderValues: action.bound,
  createDocumentFromTemplate: action.bound,
  fileNameModified: observable,
  logDescriptionModified: observable,
  modified: computed,
  previewDocumentURL: computed,
};

export default class CreateDocumentFromTemplateStore extends TemplateBaseStore {
  private forReporting = false;

  public folderLoadable: Loadable<{activeFolder: Folder; rootFolder: Folder}>;
  public templateLoadable: Loadable<Template[]>;
  public chooseTemplateData: ChooseDocumentTemplateData;

  private fileNameModified = false;
  private logDescriptionModified = false;
  public isCreatingDocument = false;

  public constructor({
    loggedInUser,
    originatingCasusId,
    originatingFolderId,
    forReporting = false,
  }: {
    loggedInUser: LoggedInUserInformation;
    originatingCasusId?: string;
    originatingFolderId?: string;
    forReporting?: boolean;
  }) {
    super({loggedInUser});
    makeObservable(this, createDocumentFromTemplateStoreMobXAnnotations);

    this.templateLoadable = {state: State.Loading};
    this.folderLoadable = {state: State.Loading};
    this.chooseTemplateData = {
      casusId: originatingCasusId,
      folderId: originatingFolderId ?? '',
      templateId: '',
      templateFileId: '',
      localeId: '',
      fileName: '',
      logDescription: '',
    };

    this.forReporting = forReporting;
    this.getTemplates(forReporting);
  }

  public getTemplateName(): string | undefined {
    if (!this.chooseTemplateData.templateId || !this.templateLoadable.data) {
      return;
    }
    return this.templateLoadable.data?.find(
      (item) => item.id === this.chooseTemplateData.templateId
    )?.text;
  }

  public async fetchFolderOverview(
    casusId: string,
    folderId: string
  ): Promise<void> {
    this.folderLoadable.state = State.Loading;
    this.folderLoadable.abort?.();

    await documentsBackend.getFolderHierarchy({
      parameters: {
        casusId,
        folderId,
      },
      abortCallbackSetter: (abortCallback) => {
        this.folderLoadable.abort = abortCallback;
      },
      onSuccess: ({body}) => {
        const {rootFolder: rootFolderData} = body;
        const rootFolder = Folder.createRootCasusFolder(rootFolderData.folder);
        const innerMostFolder = rootFolder.setNestedSubFolders(rootFolderData);
        runInAction(() => {
          this.folderLoadable.data = {
            activeFolder: innerMostFolder,
            rootFolder,
          };
          this.folderLoadable.state = State.Success;
        });
      },
      afterErrorHandler: () => {
        runInAction(() => (this.folderLoadable.state = State.Error));
      },
    });
  }

  private async getTemplates(forReporting: boolean): Promise<void> {
    this.templateLoadable.state = State.Loading;
    this.templateLoadable.abort?.();

    const payload: GetTemplatesPayLoad = {
      parameters: {},
      abortCallbackSetter: (abortCallback) => {
        this.templateLoadable.abort = abortCallback;
      },
      onSuccess: ({body}) => {
        const templatesItems = body.templates.map(
          ({id, title, templateFiles}) => ({id, templateFiles, text: title})
        );
        runInAction(() => {
          this.templateLoadable.data = templatesItems;
          this.templateLoadable.state = State.Success;
        });
      },
      afterErrorHandler: () => {
        runInAction(() => {
          this.templateLoadable.state = State.Error;
        });
      },
    };

    await (forReporting
      ? documentsBackend.getReportTemplates(payload)
      : documentsBackend.getDocumentTemplates(payload));
  }

  private getTemplateFiles(templateId: string): TemplateFile[] {
    return (
      this.templateLoadable.data?.find(
        (availableTemplate) => availableTemplate.id === templateId
      )?.templateFiles ?? []
    );
  }

  public getTemplateFile(
    templateId: string,
    localeId: string
  ): TemplateFile | undefined {
    return this.getTemplateFiles(templateId).find(
      ({locale}) => locale === localeId
    );
  }

  public getLocalesForSelectedTemplate(
    templateId: string,
    locales: TranslatedLocale[]
  ): EAutocompleteOption[] {
    return this.getTemplateFiles(templateId)
      .map(({locale: id}) => {
        const selectedLocale = locales.find(({locale}) => locale === id);

        return selectedLocale
          ? {value: id, displayText: selectedLocale.translatedName}
          : undefined;
      })
      .filter(isNotNullOrUndefined);
  }

  public setFileNameHasBeenModified(): void {
    this.fileNameModified = true;
  }

  public setLogDescriptionHasBeenModified(): void {
    this.fileNameModified = true;
  }

  public setChooseTemplateData(data: ChooseDocumentTemplateData): void {
    this.chooseTemplateData = data;
  }

  public get modified(): boolean {
    return (
      this.fileNameModified ||
      this.logDescriptionModified ||
      (this.forReporting &&
        this.wizardStep !== TemplateWizardStep.ChooseTemplate)
    );
  }

  public get previewDocumentURL(): string | undefined {
    if (this.temporaryDocumentId) {
      return documentsBackend.downloadTemporaryDocumentPdfURL({
        temporaryDocumentId: this.temporaryDocumentId,
      });
    }
  }

  public buildDocumentURL(fileName: string): string | undefined {
    if (this.temporaryDocumentId) {
      return documentsBackend.downloadTemporaryDocumentPdfURL({
        temporaryDocumentId: this.temporaryDocumentId,
        fileName,
      });
    }
  }

  private buildReportDocumentURL(
    id: string,
    fileName: string
  ): string | undefined {
    return documentsBackend.downloadTemporaryDocumentURL({
      temporaryDocumentId: id,
      fileName,
    });
  }

  public async getOverridableTemplatePlaceholders(): Promise<void> {
    await documentsBackend.getOverridableTemplatePlaceholders({
      parameters: {
        casusId: this.chooseTemplateData.casusId,
        locale: this.chooseTemplateData.localeId,
        templateFileId: this.chooseTemplateData.templateFileId,
        templateId: this.chooseTemplateData.templateId,
      },
      onSuccess: ({body}) => {
        super.getOverridableTemplatePlaceholders(body);
      },
      afterErrorHandler: () => {
        runInAction(() => {
          this.placeholderLoader.state = State.Error;
        });
      },
    });
  }

  public fetchDependablePlaceholderValues(params: {
    pageKey: string;
    parentPlaceholderKey: string;
    parentEntityId: string;
    placeholderKey: string;
  }): Promise<DefaultValueInResponse[] | void> {
    return super.fetchDependablePlaceholderValues({
      ...params,
      locale: this.chooseTemplateData.localeId,
      casusId: this.chooseTemplateData.casusId,
      templateId: this.chooseTemplateData.templateId,
      templateFileId: this.chooseTemplateData.templateFileId,
    });
  }

  protected async createTemporaryDocumentFromTemplate(): Promise<void> {
    await documentsBackend.createTemporaryDocumentFromTemplate({
      parameters: {
        body: {
          ...getCreateTemporaryDocumentParams(
            this.placeholderLoader.data ?? []
          ),
          casusId: this.chooseTemplateData.casusId,
          locale: this.chooseTemplateData.localeId,
          templateFileId: this.chooseTemplateData.templateFileId,
          templateId: this.chooseTemplateData.templateId,
          async: true,
        },
      },
      onSuccess: ({body}) => {
        super.createTemporaryDocument(body);
      },
      afterErrorHandler: () => {
        runInAction(() => (this.temporaryDocumentDataState = State.Error));
      },
    });
  }

  public downloadReport(): void {
    try {
      if (this.temporaryDocumentId) {
        const fileName = `${this.getTemplateName()?.replace(
          ' ',
          '_'
        )}_${moment().format('YYYY-MM-DD-hh-mm-ss')}.pdf`;

        const downloadUrl = this.buildReportDocumentURL(
          this.temporaryDocumentId,
          fileName
        );
        if (downloadUrl) {
          downloadGeneratedFile(downloadUrl, i18n.t);
        }
      }
    } catch {
      unexpectedError();
    }
  }

  private async createReport(): Promise<string | undefined> {
    let documentId: string | undefined;
    await documentsBackend.createTemporaryDocumentFromTemplate({
      parameters: {
        body: {
          ...getCreateTemporaryDocumentParams(
            this.placeholderLoader.data ?? []
          ),
          casusId: this.chooseTemplateData.casusId,
          locale: this.chooseTemplateData.localeId,
          templateFileId: this.chooseTemplateData.templateFileId,
          templateId: this.chooseTemplateData.templateId,
          async: false,
          reportFormat: 'PDF',
        },
      },
      onSuccess: ({body}) => {
        documentId = body.temporaryDocumentId;
      },
    });
    return documentId;
  }

  public async createDocumentFromTemplate(): Promise<string | undefined> {
    if (
      !this.temporaryDocumentId ||
      !this.chooseTemplateData.folderId ||
      !this.chooseTemplateData.casusId
    ) {
      return;
    }
    this.isCreatingDocument = true;
    let documentId: string | undefined;
    await documentsBackend.createDocumentFromTemporaryDocument({
      parameters: {
        body: {
          documentTitle: this.chooseTemplateData.fileName,
          temporaryDocumentId: this.temporaryDocumentId,
          logDescription: this.chooseTemplateData.logDescription || undefined,
        },
        casusId: this.chooseTemplateData.casusId,
        folderId: this.chooseTemplateData.folderId,
      },
      onSuccess: ({body}) => {
        runInAction(() => (this.fileNameModified = false));
        documentId = (body as CreateDocumentResponse).createdDocument.id;
      },
      localHandlers: {
        NonUniqueItemNameCondition: (condition) => {
          showSnackbar({
            text: i18n.t(
              'documents:snackbar.createDocumentFromTemplate.nonUniqueItemNameCondition',
              {
                name: (condition as NonUniqueItemNameCondition)
                  .duplicateDocumentOrFolderName,
              }
            ),
            type: SnackbarType.WARNING,
          });
        },
      },
    });
    runInAction(() => (this.isCreatingDocument = false));
    return documentId;
  }
}
